<template>
  <div class="bp-filter">
    <b-row>
      <b-col v-if="showCustomer" sm="6" md="3" class="mb-1">
        <label class="bp-filter__label" :for="id('customer')">
          {{ $t('Customer') }}
        </label>
        <v-select
          :input-id="id('customer')"
          v-model="draft.customer_id"
          :options="customerOptions"
          :reduce="opt => opt.value"
          :placeholder="$t('Select Customer')"
          @input="apply"
        />
      </b-col>
      <b-col sm="6" md="3" class="mb-1">
        <label class="bp-filter__label" :for="id('provider')">
          {{ $t('Provider') }}
        </label>
        <v-select
          :input-id="id('provider')"
          v-model="draft.provider_id"
          :options="providerOptions"
          :reduce="opt => opt.value"
          :placeholder="$t('Select Provider')"
          @input="apply"
        />
      </b-col>
      <b-col sm="6" md="3" class="mb-1">
        <label class="bp-filter__label" :for="id('type')">
          {{ $t('Barcode Type') }}
        </label>
        <v-select
          :input-id="id('type')"
          v-model="draft.barcode_type"
          :options="barcodeTypeOptions"
          :reduce="opt => opt.value"
          :placeholder="$t('All Types')"
          @input="apply"
        />
      </b-col>
      <b-col sm="6" md="3" class="mb-1">
        <label class="bp-filter__label" :for="id('status')">
          {{ $t('Status') }}
        </label>
        <v-select
          :input-id="id('status')"
          v-model="draft.status"
          :options="statusOptions"
          :reduce="opt => opt.value"
          :placeholder="$t('All Statuses')"
          @input="apply"
        />
      </b-col>
    </b-row>

    <div v-if="hasActiveFilters" class="bp-filter__badges">
      <b-badge
        v-for="(value, key) in activeFilters"
        :key="key"
        variant="light-primary"
        class="bp-filter__badge"
      >
        {{ labelFor(key) }}: {{ displayValue(key, value) }}
        <feather-icon
          icon="XIcon"
          size="12"
          class="ml-50 cursor-pointer"
          role="button"
          :aria-label="$t('Remove filter')"
          @click="clearOne(key)"
        />
      </b-badge>
      <b-button variant="link" size="sm" class="p-0 ml-50" @click="clearAll">
        {{ $t('Clear') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { BRow, BCol, BBadge, BButton } from 'bootstrap-vue';
import vSelect from 'vue-select';
import { helper } from '@core/mixins/helper';

let instanceCounter = 0;

export default {
  name: 'BarcodePoolFilter',

  mixins: [helper],

  components: {
    BRow,
    BCol,
    BBadge,
    BButton,
    vSelect
  },

  props: {
    values: {
      type: Object,
      required: true
    },
    providerOptions: {
      type: Array,
      default: () => []
    },
    customerOptions: {
      type: Array,
      default: () => []
    },
    showCustomer: {
      type: Boolean,
      default: false
    }
  },

  data() {
    instanceCounter += 1;
    return {
      draft: { ...this.values },
      instanceId: instanceCounter
    };
  },

  computed: {
    barcodeTypeOptions() {
      return [
        { label: this.$t('Small Package'), value: 'small' },
        { label: this.$t('Large Package'), value: 'large' }
      ];
    },

    statusOptions() {
      return [
        { label: this.$t('Active'), value: 'active' },
        { label: this.$t('Exhausted'), value: 'exhausted' },
        { label: this.$t('All'), value: 'all' }
      ];
    },

    activeFilters() {
      const out = {};
      Object.keys(this.draft).forEach(key => {
        const v = this.draft[key];
        if (v !== null && v !== '' && v !== undefined) out[key] = v;
      });
      return out;
    },

    hasActiveFilters() {
      return Object.keys(this.activeFilters).length > 0;
    }
  },

  watch: {
    values(val) {
      this.draft = { ...val };
    }
  },

  methods: {
    id(key) {
      return `bp-filter-${this.instanceId}-${key}`;
    },

    apply() {
      this.$emit('filter', this.getFilteredObject(this.draft));
    },

    clearOne(key) {
      this.$delete(this.draft, key);
      this.apply();
    },

    clearAll() {
      this.draft = {};
      this.apply();
    },

    labelFor(key) {
      const map = {
        customer_id: this.$t('Customer'),
        provider_id: this.$t('Provider'),
        barcode_type: this.$t('Barcode Type'),
        status: this.$t('Status')
      };
      return map[key] || key;
    },

    displayValue(key, value) {
      const sources = {
        customer_id: this.customerOptions,
        provider_id: this.providerOptions,
        barcode_type: this.barcodeTypeOptions,
        status: this.statusOptions
      };
      const source = sources[key];
      if (!source) return value;
      const option = source.find(o => o.value === value);
      return option ? option.label : value;
    }
  }
};
</script>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';
@import '@core/scss/vue/libs/vue-select.scss';

.bp-filter {
  padding-bottom: 1rem;

  &__label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $text-muted;
    font-weight: 600;
    margin-bottom: 0.35rem;
    display: block;
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    padding-top: 0.25rem;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.7rem;
    font-weight: 500;
    font-size: 0.75rem;
  }
}
</style>
